<template>
    <div id="app">
        <div v-if="initOk">
            <div class="app-nav-bar">
                <div class="app-title">融亦投运维平台</div>
                <div class="app-nav-menu">
                    <div v-for="app in apps" :key="app.activeRule" class="app-nav-menu-item"
                         :class="app === currentApp ? 'app-nav-menu-item-selected' : ''"
                         @click="push(app.activeRule)">投研平台</div>
                </div>
                <div class="app-user">
                    <div class="app-user-info">{{user.name}}</div>
                    <div class="app-user-menu">
                        <div class="app-user-menu-item" @click="gotoOldVersion">返回旧版</div>
                        <div class="app-user-menu-item" @click="logout">退出</div>
                    </div>
                </div>
            </div>
            <div id="microapp-container"></div>
        </div>
        <div class="app-error" v-if="!initOk && error">{{error}}</div>
    </div>
</template>

<script>
    import {initGlobalState, registerMicroApps, runAfterFirstMounted, setDefaultMountApp, start} from 'qiankun'
    import axios from './plugins/axios'
    import URL from 'url-parse'
    import config from './config'
    import qs from 'qs'

    export default {
        name: 'App',
        data() {
            return {
                user: null,
                token: null,
                permissions: [],
                initOk: false,
                error: null,
                apps: this.prepareApps()
            }
        },
        created() {
            if (!window.__INJECTED_PUBLIC_PATH_BY_QIANKUN__) {
                this.init()
            } else {
                // fix hot-reload
                location.reload()
            }
        },
        computed: {
            currentApp() {
                const currentPath = document.location.pathname
                const publicPath = process.env.BASE_URL
                for (const app of this.apps) {
                    const appPath = publicPath + app.activeRule.substring(1)
                    if (appPath.startsWith(currentPath)) {
                        return app
                    }
                }
                return this.apps[0]
            }
        },
        methods: {
            prepareApps() {
                const commonProps = {
                    hostService: {
                        isLogin: () => this.isLogin(),
                        hasPermission: (app, module, op, obj) => this.hasPermission(app, module, op, obj),
                        getUser: () => this.user,
                        getToken: () => this.token,
                        getAuthorizationHeader: () => this.authorizationHeader()
                    }
                }
                const preparedApps = []
                for (const app of config.apps) {
                    const preparedApp = JSON.parse(JSON.stringify(app))
                    preparedApp.container = '#microapp-container'
                    if (!preparedApp.props) {
                        preparedApp.props = {}
                    }
                    Object.assign(preparedApp.props, commonProps)
                    preparedApps.push(preparedApp)
                }
                return preparedApps
            },
            async init() {
                if (await this.login()) {
                    this.initQiankun()
                }
            },
            async login() {
                const url = new URL(document.location.href)
                if (url.hash && url.hash.indexOf('access_token') !== -1) {
                    const token = qs.parse(url.hash.substring(1))
                    if (token['access_token']) {
                        this.token = token
                        window.localStorage.setItem('token', JSON.stringify(token))
                        const lastUrl = window.sessionStorage.getItem('lastUrl')
                        if (lastUrl) {
                            window.sessionStorage.removeItem('lastUrl')
                            history.replaceState(null, '', lastUrl)
                        }
                    }
                }
                if (!this.token) {
                    const tokenStr = window.localStorage.getItem('token')
                    if (tokenStr) {
                        this.token = JSON.parse(tokenStr)
                    }
                }
                if (!this.token) {
                    this.requestSsoLogin()
                    return false
                }
                try {
                    this.user = (await this.getUserInfo()).data
                    this.permissions = (await this.getUserPermissions()).data
                    this.initOk = true
                    return true
                } catch (e) {
                    if (e.response && e.response.status === 401) {
                        // token失效，需要重新登录
                        this.requestSsoLogin()
                        return false
                    } else {
                        this.initOk = false
                        this.error = "系统出错，请稍候重试！"
                        throw e
                    }
                }
            },
            requestSsoLogin() {
                if (!window.sessionStorage.getItem('lastUrl')) {
                    window.sessionStorage.setItem('lastUrl', document.location.href)
                }
                const params = {
                    session: '',
                    response_type: 'token',
                    client_id: config.clientId,
                    redirect_uri: config.baseUrl
                }
                const authorizeUrl = config.dpsUri + 'app/omp/oauth/authorize?' + qs.stringify(params)
                document.location.replace(authorizeUrl)
            },
            getUserInfo() {
                // 获取用户信息
                return axios({
                    url: config.dpsUri + 'app/omp/rest/security/user',
                    method: 'GET',
                    headers: this.authorizationHeader()
                })
            },
            getUserPermissions() {
                // 获取用户权限
                return axios({
                    url: config.dpsUri + 'app/omp/rest/security/user/permissions',
                    method: 'GET',
                    headers: this.authorizationHeader()
                })
            },
            authorizationHeader() {
                if (this.token) {
                    return {
                        Authorization: this.token.token_type + ' ' + this.token.access_token
                    }
                }
                return null
            },
            initQiankun() {
                registerMicroApps(
                    this.apps
                )
                const {onGlobalStateChange} = initGlobalState({})
                onGlobalStateChange(() => {})
                setDefaultMountApp(this.apps[0].activeRule)
                runAfterFirstMounted(() => {})
                start({prefetch: true})
            },
            push(microapp) {
                if (this.currentApp.activeRule !== microapp) {
                    history.pushState(null, '', microapp)
                }
            },
            isLogin() {
                return this.user ? true : false
            },
            hasPermission(app, module, op, obj) {
                for (let p of this.permissions) {
                    if (p.application === app && p.module === module && p.operation === op && p.object === obj) {
                        return true
                    }
                }
                return false
            },
            logout() {
                window.localStorage.removeItem('token')
                document.location.assign(config.dpsUri + 'app/logout?redirect=/')
            },
            gotoOldVersion(){
                document.location = config.dpsUri + 'app/omp/'
            }
        }
    }
</script>

<style scoped>
    .app-nav-bar {
        display: flex;
        flex-direction: row;
        align-items: center;
        height: 50px;
        background-color: #409EFF;
        color: #ffffff;
    }
    .app-title {
        margin-left: 20px;
        font-size: 20px;
    }
    .app-nav-menu {
        flex: 1;
        display: flex;
        margin-left: 20px;
    }
    .app-nav-menu-item {
        cursor: pointer;
        font-size: 16px;
    }
    .app-user {
        display: flex;
        font-size: 14px;
    }
    .app-user-menu {
        margin-left: 10px;
        display: flex;
        margin-right: 10px;
    }
    .app-user-menu-item {
        cursor: pointer;
    }
    .app-user-menu-item+.app-user-menu-item{
        margin-left: 5px;
    }
    .app-error {
        font-size: 14px;
        color: #ff0000;
    }
    .app-nav-menu-item-selected{
        color: #ff0000;
    }
</style>
